import {
  Button,
  CallToAction,
  Client,
  ResponsiveImage,
  Job,
  Fact,
  Facts,
  Faq,
  Testimonial,
  Features,
  Iframe,
  Media,
  Press,
  Presses,
  PullQuote,
  Table,
  Teaser,
  TeaserBlock,
  Video,
  VideoExternal
} from '~/graphql/fragments'
import { graphql } from '~/lib/datocms/graphql'

/**
 * GraphQL fragment needed for the component to function.
 * Learn more: https://gql-tada.0no.co/guides/fragment-colocation
 */

export const SectionAwards = graphql(
  /* GraphQL */ `
    fragment SectionAwards on SectionAwardsListRecord {
      blocks {
        name
        media {
          alt
          url
          width
          height
          responsiveImage(
            imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "1:1", w: 150 }
          ) {
            ...ResponsiveImage
          }
        }
      }
    }
  `,
  [ResponsiveImage]
)

export const SectionClients = graphql(
  /* GraphQL */ `
    fragment SectionClients on SectionClientListRecord {
      id
      title
      records {
        ...Client
      }
      invertColorTheme
    }
  `,
  [Client]
)

export const SectionNewsletterSubscription = graphql(/* GraphQL */ `
  fragment SectionNewsletterSubscription on SectionNewsletterSubscriptionRecord {
    title
    description
    disclaimer
    thankYouMessage
    invertColorTheme
  }
`)

export const SectionCta = graphql(
  /* GraphQL */ `
    fragment SectionCta on SectionCtaRecord {
      id
      buttons {
        ...CallToAction
      }
      html
      title
    }
  `,
  [CallToAction]
)

export const SectionHero = graphql(
  /* GraphQL */ `
    fragment SectionHero on SectionHeroRecord {
      label
      title
      subtitle
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 1440 }
        ) {
          ...ResponsiveImage
        }
        standardRatio: url(imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, w: 1920 })
        cinemaRatio: url(
          imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, w: 1105, h: 510 }
        )
      }
      mediaFit
      buttons {
        ...CallToAction
      }
      layout
      invertColorTheme
    }
  `,
  [ResponsiveImage, CallToAction]
)

export const SectionHome = graphql(/* GraphQL */ `
  fragment SectionHome on SectionHomeRecord {
    title
    reel {
      title
      videoLoop {
        url
        mimeType
        height
        width
        video {
          duration
          framerate
          mp4Url(res: medium, exactRes: medium)
          thumbnailUrl
          streamingUrl
          muxPlaybackId
          muxAssetId
        }
      }
      video {
        url
        mimeType
        height
        width
        video {
          duration
          framerate
          mp4Url(res: medium, exactRes: medium)
          thumbnailUrl
          streamingUrl
          muxPlaybackId
          muxAssetId
        }
      }
    }
    invertColorTheme
  }
`)

export const SectionJobs = graphql(
  /* GraphQL */ `
    fragment SectionJobs on SectionJobListRecord {
      title
      subtitle
      buttons {
        ...CallToAction
      }
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 1440 }
        ) {
          ...ResponsiveImage
        }
        standardRatio: url(imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, w: 1920 })
        cinemaRatio: url(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "21:9", w: 1428 }
        )
      }
      boardTitle
      records {
        ...Job
      }
      invertColorTheme
    }
  `,
  [Job]
)

export const SectionStructuredText = graphql(
  /* GraphQL */ `
    fragment SectionStructuredText on SectionStructuredTextRecord {
      title
      subtitle
      structuredText {
        blocks {
          ... on RecordInterface {
            id
            _modelApiKey
            __typename
          }
          ...Button
          ...Facts
          ...Features
          ...Iframe
          ...Media
          ...Press
          ...Presses
          ...PullQuote
          ...Table
          ...TeaserBlock
          ...Video
          ...VideoExternal
        }
        links {
          ... on RecordInterface {
            id
            _modelApiKey
            __typename
          }
          ... on ArticleRecord {
            slug
            title
          }
          ... on PageRecord {
            title
            slug
          }
          ... on WorkRecord {
            slug
            title
          }
        }
        value
      }
      invertColorTheme
    }
  `,
  [
    Button,
    Facts,
    Features,
    Iframe,
    Media,
    Press,
    Presses,
    PullQuote,
    Table,
    TeaserBlock,
    Video,
    VideoExternal
  ]
)

export const SectionCanvas = graphql(/* GraphQL */ `
  fragment SectionCanvas on SectionCanvasXpRecord {
    _modelApiKey
    id
    title
    subtitle
    canvasName
    invertColorTheme
  }
`)

export const SectionImageGallery = graphql(
  /* GraphQL */ `
    fragment SectionImageGallery on SectionImageGalleryRecord {
      id
      images {
        id
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "1:1", w: 406 }
        ) {
          ...ResponsiveImage
        }
      }
      title
    }
  `,
  [ResponsiveImage]
)

export const SectionContactForm = graphql(/* GraphQL */ `
  fragment SectionContactForm on SectionContactFormRecord {
    title
    textHtml
    emailForSubmissions
    thankYouMessage
    invertColorTheme
  }
`)

export const SectionFacts = graphql(
  /* GraphQL */ `
    fragment SectionFacts on SectionFactListRecord {
      id
      title
      facts {
        ...Fact
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Faq]
)

export const SectionFaq = graphql(
  /* GraphQL */ `
    fragment SectionFaq on SectionFaqRecord {
      id
      heading
      subheading
      bft
      mediaBackground {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 1280 }
        ) {
          ...ResponsiveImage
        }
      }
      records {
        ...Faq
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Faq]
)

export const SectionFeatures = graphql(/* GraphQL */ `
  fragment SectionFeatures on SectionFeaturesListRecord {
    id
    title
    subtitle
    features {
      title
      body
    }
    invertColorTheme
  }
`)

export const SectionGames = graphql(/* GraphQL */ `
  fragment SectionGames on SectionGameListRecord {
    _modelApiKey
    id
    categories {
      id
      name
      slug
    }
    itemsPerPage
    linkMore
    title
    showFilters
    invertColorTheme
  }
`)

export const SectionLetsTalk = graphql(
  /* GraphQL */ `
    fragment SectionLetsTalk on SectionLetsTalkRecord {
      _modelApiKey
      id
      textOne
      textTwo
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, w: 860, h: 360 }
        ) {
          ...ResponsiveImage
        }
      }
    }
  `,
  [ResponsiveImage]
)

export const SectionLogos = graphql(
  /* GraphQL */ `
    fragment SectionLogos on SectionLogosListRecord {
      id
      title
      records {
        ...Client
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage]
)

export const SectionMedia = graphql(
  /* GraphQL */ `
    fragment SectionMedia on SectionMediaBlockRecord {
      title
      html
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "4:3", w: 624 }
        ) {
          ...ResponsiveImage
        }
      }
      buttons {
        ...CallToAction
      }
      mediaPosition
      invertColorTheme
    }
  `,
  [ResponsiveImage, CallToAction]
)

export const SectionMediaLarge = graphql(
  /* GraphQL */ `
    fragment SectionMediaLarge on SectionMediaLargeRecord {
      title
      html
      mediaAssets {
        __typename
        title
        customData
        url
        width
        height
        alt
        mimeType
        title
        format
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "9:10", w: 670 }
        ) {
          srcSet
          webpSrcSet
          sizes
          src
          width
          height
          aspectRatio
          alt
          title
          bgColor
          base64
        }
        srcNarrow: url(
          imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, ar: "21:9", w: 1280 }
        )
        srcSquare: url(
          imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, ar: "1:1", w: 325 }
        )
      }
      buttons {
        ...CallToAction
      }
      invertColorTheme
    }
  `,
  [CallToAction]
)

export const SectionReel = graphql(
  /* GraphQL */ `
    fragment SectionReel on SectionReelRecord {
      record {
        title
        image {
          __typename
          title
          customData
          url
          width
          height
          alt
          mimeType
          title
          format
          responsiveImage(
            imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 1280 }
          ) {
            ...ResponsiveImage
          }
        }
        videoLoop {
          url
          mimeType
          height
          width
          video {
            duration
            framerate
            mp4Url(res: medium, exactRes: medium)
            thumbnailUrl
            streamingUrl
            muxPlaybackId
            muxAssetId
          }
        }
        video {
          url
          mimeType
          height
          width
          video {
            duration
            framerate
            mp4Url(res: medium, exactRes: medium)
            thumbnailUrl
            streamingUrl
            muxPlaybackId
            muxAssetId
          }
        }
        slug
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Button]
)

export const SectionContactUs = graphql(
  /* GraphQL */ `
    fragment SectionContactUs on SectionContactRecord {
      title
      subtitle
      buttons {
        ...Button
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Button]
)

export const SectionStatement = graphql(
  /* GraphQL */ `
    fragment SectionStatement on SectionStatementRecord {
      title
      subtitle
      mediaAsset {
        __typename
        title
        customData
        url
        width
        height
        alt
        mimeType
        title
        format
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "4:3", w: 880 }
        ) {
          ...ResponsiveImage
        }
      }
      buttons {
        ...CallToAction
      }
      text
      invertColorTheme
    }
  `,
  [ResponsiveImage, CallToAction]
)

export const SectionNumbers = graphql(
  /* GraphQL */ `
    fragment SectionNumbers on SectionNumbersBlockRecord {
      title
      blocks {
        ...Fact
      }
      titleTestimonials
      testimonials {
        ...Testimonial
      }
      invertColorTheme
    }
  `,
  [Fact, Testimonial]
)

export const SectionTestimonials = graphql(
  /* GraphQL */ `
    fragment SectionTestimonials on SectionTestimonialsListRecord {
      id
      title
      testimonials {
        ...Testimonial
      }
    }
  `,
  [Testimonial]
)

export const SectionVideo = graphql(/* GraphQL */ `
  fragment SectionVideo on SectionVideoRecord {
    id
    title
    video {
      url
      title
      thumbnailUrl
      height
      provider
      providerUid
      width
    }
    html
  }
`)

export const SectionServices = graphql(
  /* GraphQL */ `
    fragment SectionServices on SectionServicesListRecord {
      heading
      services {
        title
        slug
        image {
          alt
          url
          width
          height
          responsiveImage(
            imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, w: 1024, h: 576 }
          ) {
            ...ResponsiveImage
          }
        }
        description
      }
      callToAction {
        ...CallToAction
      }
      layout
      invertColorTheme
    }
  `,
  [ResponsiveImage, CallToAction]
)

export const SectionWorks = graphql(/* GraphQL */ `
  fragment SectionWorks on SectionWorkListRecord {
    _modelApiKey
    id
    categories {
      id
      name
      slug
    }
    itemsPerPage
    linkMore
    title
    invertColorTheme
  }
`)

export const SectionTeam = graphql(
  /* GraphQL */ `
    fragment SectionTeam on SectionTeamRecord {
      id
      title
      records {
        name
        media {
          alt
          url
          width
          height
          responsiveImage(
            imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "4:3", w: 320 }
          ) {
            ...ResponsiveImage
          }
        }
        role
      }
      button {
        ...Button
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Button]
)

export const SectionAbout = graphql(
  /* GraphQL */ `
    fragment SectionAbout on SectionAboutRecord {
      title
      html
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "1:1", w: 896 }
        ) {
          ...ResponsiveImage
        }
      }
      buttons {
        ...CallToAction
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Button]
)

export const SectionAboutPage = graphql(/* GraphQL */ `
  fragment SectionAboutPage on SectionAboutPageRecord {
    _modelApiKey
    id
    title
    subtitle
    invertColorTheme
  }
`)

export const SectionArticles = graphql(/* GraphQL */ `
  fragment SectionArticles on SectionArticleListRecord {
    _modelApiKey
    id
    categories {
      id
      name
      slug
    }
    itemsPerPage
    linkMore
    title
    invertColorTheme
  }
`)

export const SectionRelatedArticles = graphql(
  /* GraphQL */ `
    fragment SectionRelatedArticles on SectionRelatedArticlesListRecord {
      _modelApiKey
      id
      title
      categories {
        id
        name
      }
      overrideArticles {
        _modelApiKey
        category {
          id
          name
        }
        excerpt
        id
        title
        slug
        image {
          alt
          url
          width
          height
          responsiveImage(
            imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 608 }
          ) {
            ...ResponsiveImage
          }
        }
      }
      buttons {
        ...Button
      }
      itemsPerPage
    }
  `,
  [ResponsiveImage, Button]
)

export const SectionTeaser = graphql(
  /* GraphQL */ `
    fragment SectionTeaser on SectionTeaserRecord {
      id
      title
      records {
        ...Teaser
      }
      invertColorTheme
    }
  `,
  [ResponsiveImage, Teaser]
)

export const SectionProjects = graphql(/* GraphQL */ `
  fragment SectionProjects on SectionProjectListRecord {
    _modelApiKey
    id
    title
    records {
      _modelApiKey
      title
      slug
      image {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "16:9", w: 608 }
        ) {
          ...ResponsiveImage
        }
      }
      imageSquared: image {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fm: webp, fit: crop, ar: "1:1", w: 608 }
        ) {
          ...ResponsiveImage
        }
      }
      clients {
        name
      }
      category {
        id
        name
        slug
      }
      videoLoop {
        url
        mimeType
        height
        width
        video {
          duration
          framerate
          mp4Url(res: medium, exactRes: medium)
          thumbnailUrl
          streamingUrl
          muxPlaybackId
          muxAssetId
        }
      }
    }
    buttons {
      ...CallToAction
    }
    layout
    invertColorTheme
  }
`)
